import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { 
  Add, 
  Delete, 
  Save, 
  Edit,
  Work
} from '@mui/icons-material';
import { MediaPreview } from './components/MediaPreview';
import { MediaManager } from './components/MediaManager';
import { getStaticFileUrl } from '../utils/config';
import { useAuth } from '../context/AuthContext';

interface PortfolioCard {
  id: string;
  imgSrc: string;
  logoSrc: string;
  title: string;
  description: string;
}

interface PortfolioData {
  cards: PortfolioCard[];
}

// 기본 카드 데이터
const defaultCards: PortfolioCard[] = [
  {
    id: '1',
    imgSrc: "/img/home/SBS생방송투데이.png",
    logoSrc: "/img/logo/sbs.png",
    title: "SBS",
    description: "SBS 생방송투데이",
  },
  {
    id: '2',
    imgSrc: "/img/home/SBS꾸러기탐구생활.png",
    logoSrc: "/img/logo/sbs.png",
    title: "SBS",
    description: "SBS 꾸러기탐구생활",
  },
  {
    id: '3',
    imgSrc: "/img/home/SBS물은생명이다.png",
    logoSrc: "/img/logo/sbs.png",
    title: "SBS",
    description: "SBS 물은 생명이다",
  },
  {
    id: '4',
    imgSrc: "/img/home/EBS직업대학미래학과.png",
    logoSrc: "/img/logo/ebs.png",
    title: "EBS",
    description: "EBS 직업대학 미래학과",
  },
  {
    id: '5',
    imgSrc: "/img/home/한국산업인력공단(잡플러스TV).png",
    logoSrc: "/img/logo/한국산업인력공단.png",
    title: "한국산업인력공단",
    description: "한국산업인력공단 잡플러스TV 2024년 직업능력개발 디지털콘텐츠 제작 및 유튜브 운영",
  },
  {
    id: '6',
    imgSrc: "/img/home/EBSAIPENGTALK.png",
    logoSrc: "/img/logo/ebs.png",
    title: "EBS",
    description: "EBS AI PENGTALK",
  },
  {
    id: '7',
    imgSrc: "/img/home/EBSjoyvillage.png",
    logoSrc: "/img/logo/ebs.png",
    title: "EBS",
    description: "EBS Joy Village",
  },
];

// 고유 ID 생성 함수
const generateUniqueId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const PortfolioManager: React.FC = () => {
  const { logout } = useAuth();
  const [portfolioData, setPortfolioData] = useState<PortfolioData>({
    cards: defaultCards,
  });
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'image' | 'video'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);
  
  // 편집 관련 상태
  const [editingCard, setEditingCard] = useState<PortfolioCard | null>(null);
  const [originalCard, setOriginalCard] = useState<PortfolioCard | null>(null);
  const [showCardDialog, setShowCardDialog] = useState(false);
  
  // 임시 업로드 관리
  const [tempUploads, setTempUploads] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    loadPortfolioData();
  }, []);

  const loadPortfolioData = async () => {
    setLoading(true);
    try {
      const { portfolioApi } = await import('../services/api');
      
      // 홈 포트폴리오 데이터를 가져오기
      const homePortfolioData = await portfolioApi.getHomePortfolioData();
      if (homePortfolioData && homePortfolioData.cards) {
        setPortfolioData({
          cards: homePortfolioData.cards || defaultCards,
        });
      }
    } catch (error) {
      console.error('포트폴리오 데이터 로드 실패:', error);
      setErrorMessage('포트폴리오 데이터를 불러오는데 실패했습니다.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCardChange = (index: number, field: keyof PortfolioCard, value: string) => {
    setPortfolioData(prev => ({
      cards: prev.cards.map((card, i) => 
        i === index ? { ...card, [field]: value } : card
      )
    }));
  };

  const handleImageUpload = (index: number, field: 'imgSrc' | 'logoSrc') => (fileUrl: string) => {
    handleCardChange(index, field, fileUrl);
  };

  const handleAddCard = () => {
    const newCard: PortfolioCard = {
      id: generateUniqueId(),
      imgSrc: '',
      logoSrc: '',
      title: '',
      description: '',
    };
    setEditingCard({ ...newCard }); // 깊은 복사
    setOriginalCard(null);
    setShowCardDialog(true);
  };

  const handleEditCard = (card: PortfolioCard) => {
    setEditingCard({ ...card }); // 깊은 복사
    setOriginalCard({ ...card }); // 원본 데이터 보존
    setShowCardDialog(true);
  };

  const handleDeleteCard = async (cardId: string) => {
    try {
      // 삭제할 카드 확인
      const cardToDelete = portfolioData.cards.find(card => card.id === cardId);
      if (!cardToDelete) {
        setErrorMessage('삭제할 카드를 찾을 수 없습니다.');
        setShowError(true);
        return;
      }

      // 사용자 확인
      if (!window.confirm(`"${cardToDelete.title}" 카드를 삭제하시겠습니까?`)) {
        return;
      }

      // 개별 카드 삭제 API 사용
      const { portfolioApi } = await import('../services/api');
      const response = await portfolioApi.deleteHomePortfolioCard(cardId);

      // 서버 응답 확인 후 로컬 상태 업데이트
      if (response && response.data) {
        setPortfolioData({
          cards: response.data.cards || [],
        });
        setShowSuccess(true);
      } else {
        // 서버 응답이 없으면 로컬에서만 제거
        const updatedCards = portfolioData.cards.filter(card => card.id !== cardId);
        setPortfolioData({
          cards: updatedCards,
        });
        setShowSuccess(true);
      }
    } catch (error) {
      console.error('삭제 실패:', error);
      setErrorMessage('삭제에 실패했습니다.');
      setShowError(true);
    }
  };

  const handleSaveCard = async () => {
    if (!editingCard) return;

    try {
      let updatedCards: PortfolioCard[];

      if (originalCard) {
        // 수정 모드
        updatedCards = portfolioData.cards.map(card =>
          card.id === editingCard.id ? editingCard : card
        );
      } else {
        // 추가 모드
        updatedCards = [...portfolioData.cards, editingCard];
      }

      // 서버에 저장
      const { portfolioApi } = await import('../services/api');
      const response = await portfolioApi.updateHomePortfolioData({ cards: updatedCards });

      if (response && response.data) {
        setPortfolioData({ cards: response.data.cards || updatedCards });
      } else {
        setPortfolioData({ cards: updatedCards });
      }

      // 저장 완료 - 임시 업로드는 유지 (파일 삭제하지 않음)
      setEditingCard(null);
      setOriginalCard(null);
      setShowCardDialog(false);
      setShowSuccess(true);
    } catch (error) {
      console.error('저장 실패:', error);
      setErrorMessage('저장에 실패했습니다.');
      setShowError(true);
    }
  };

  const handleCancelEdit = () => {
    // 임시 업로드된 파일들 정리
    Object.values(tempUploads).forEach(async (fileUrl) => {
      try {
        const urlParts = fileUrl.split('/');
        const type = urlParts[urlParts.length - 2]; // image 또는 video
        const filename = urlParts[urlParts.length - 1];
        
        const { uploadApi } = await import('../services/api');
        await uploadApi.deleteFile(type, filename);
      } catch (error) {
        console.error('임시 파일 삭제 실패:', error);
      }
    });
    
    // 임시 업로드 상태 초기화
    setTempUploads({});
    setEditingCard(null);
    setOriginalCard(null);
    setShowCardDialog(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { portfolioApi } = await import('../services/api');
      
      // 홈 포트폴리오 데이터 저장
      await portfolioApi.updateHomePortfolioData({
        cards: portfolioData.cards,
      });

      setShowSuccess(true);
    } catch (error) {
      console.error('저장 실패:', error);
      setErrorMessage('저장에 실패했습니다.');
      setShowError(true);
    } finally {
      setSaving(false);
    }
  };

  // 임시 업로드 처리
  const handleTempUpload = (media: any) => {
    if (!editingCard) return;
    
    // 어떤 필드가 업데이트되었는지 확인
    const field = media.url.includes('image') ? 'imgSrc' : 'logoSrc';
    setTempUploads(prev => ({ ...prev, [field]: media.url }));
  };

  // 임시 업로드 취소 처리
  const handleCancelUpload = async (mediaId: string) => {
    if (!editingCard) return;
    
    // 어떤 필드가 취소되었는지 확인
    const field = Object.keys(tempUploads).find(key => tempUploads[key] === mediaId);
    if (field) {
      try {
        const urlParts = tempUploads[field].split('/');
        const type = urlParts[urlParts.length - 2];
        const filename = urlParts[urlParts.length - 1];
        
        const { uploadApi } = await import('../services/api');
        await uploadApi.deleteFile(type, filename);
        
        // 임시 업로드에서 제거
        const newTempUploads = { ...tempUploads };
        delete newTempUploads[field];
        setTempUploads(newTempUploads);
        
        // 편집 중인 카드에서도 제거
        setEditingCard({ ...editingCard, [field]: '' });
      } catch (error) {
        console.error('임시 업로드 취소 실패:', error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('로그아웃 실패:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          포트폴리오 데이터를 불러오는 중...
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Work sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              대표 포트폴리오 관리
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            이 페이지에서 대표 포트폴리오를 관리하면 홈페이지와 About 페이지에 모두 자동으로 반영됩니다.
          </Alert>

          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                대표 포트폴리오 ({portfolioData.cards.length}개)
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddCard}
                >
                  포트폴리오 추가
                </Button>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {portfolioData.cards.map((card, index) => (
                <Grid item xs={12} sm={6} md={4} key={card.id}>
                  <Card variant="outlined">
                    <MediaPreview
                      src={card.imgSrc || '/img/placeholder.png'}
                      alt={card.title}
                      title={card.title}
                      type="image"
                      width="100%"
                      height={200}
                      showPreviewButton={false}
                      showTypeChip={false}
                    />
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box
                          component="img"
                          src={getStaticFileUrl(card.logoSrc || '/img/placeholder.png')}
                          alt="Logo"
                          sx={{ width: 40, height: 40, mr: 2, objectFit: 'contain' }}
                        />
                        <Typography variant="h6" component="div">
                          {card.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {card.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => handleEditCard(card)}
                        >
                          수정
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleDeleteCard(card.id)}
                        >
                          삭제
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              size="large"
              sx={{ minWidth: 120 }}
            >
              {saving ? '저장 중...' : '저장'}
            </Button>
          </Box>
        </Paper>

        {/* 카드 편집 다이얼로그 */}
        <Dialog 
          open={showCardDialog} 
          onClose={handleCancelEdit}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {originalCard ? '대표 포트폴리오 수정' : '새 대표 포트폴리오 추가'}
          </DialogTitle>
          <DialogContent>
            {editingCard && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    메인 이미지
                  </Typography>
                  {editingCard.imgSrc && (
                    <Box sx={{ mb: 2 }}>
                      <MediaPreview 
                        src={editingCard.imgSrc} 
                        alt="메인 이미지" 
                        width="100%"
                        height={200}
                      />
                    </Box>
                  )}
                  <MediaManager
                    onMediaSelect={(media) => setEditingCard({ ...editingCard, imgSrc: media.url })}
                    onTempUpload={handleTempUpload}
                    onCancelUpload={handleCancelUpload}
                    filterType="image"
                    showUpload={true}
                    showPreview={true}
                    showEdit={false}
                    showDelete={false}
                    multiple={false}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    로고 이미지
                  </Typography>
                  {editingCard.logoSrc && (
                    <Box sx={{ mb: 2 }}>
                      <MediaPreview 
                        src={editingCard.logoSrc} 
                        alt="로고" 
                        width="100%"
                        height={200}
                      />
                    </Box>
                  )}
                  <MediaManager
                    onMediaSelect={(media) => setEditingCard({ ...editingCard, logoSrc: media.url })}
                    onTempUpload={handleTempUpload}
                    onCancelUpload={handleCancelUpload}
                    filterType="image"
                    showUpload={true}
                    showPreview={true}
                    showEdit={false}
                    showDelete={false}
                    multiple={false}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="제목"
                    value={editingCard.title}
                    onChange={(e) => setEditingCard({ ...editingCard, title: e.target.value })}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="설명"
                    value={editingCard.description}
                    onChange={(e) => setEditingCard({ ...editingCard, description: e.target.value })}
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEdit}>
              취소
            </Button>
            <Button onClick={handleSaveCard} variant="contained">
              저장
            </Button>
          </DialogActions>
        </Dialog>

        {/* 성공/실패 알림 */}
        <Snackbar
          open={showSuccess}
          autoHideDuration={3000}
          onClose={() => setShowSuccess(false)}
        >
          <Alert onClose={() => setShowSuccess(false)} severity="success">
            대표 포트폴리오가 성공적으로 저장되었습니다!
          </Alert>
        </Snackbar>

        <Snackbar
          open={showError}
          autoHideDuration={5000}
          onClose={() => setShowError(false)}
        >
          <Alert onClose={() => setShowError(false)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}; 